import { post, get, del } from '../utils/networking';
import { QuestionType, ChartType, AnalyzeStatus } from './types/survey-enums';

export type CreateCustomSurveyFromTemplateRequest = {
  templateId: number;
  title: string;
  description?: string;
  teamId?: number;
  organizationId?: number;
  allowAnonymous?: boolean;
  deadlineInDays?: number;
  minimumResponseRate?: number;
  expectedParticipantsCount?: number;
  scheduledTime?: Date;
  frequency?: string;
};

export type CustomSurveyQuestionOption = {
  id: number;
  optionText: string;
  orderIndex: number;
};

// Base question properties shared by all question types
type BaseQuestion = {
  id: number;
  questionText: string;
  category: string | null;
  orderIndex: number;
  isRequired: boolean;
  templateQuestionId?: number;
  chartType?: ChartType | null;
  options: CustomSurveyQuestionOption[];
};

// Scale-based questions (Likert, NPS, Yes/No)
type ScaleQuestion = BaseQuestion & {
  questionType: QuestionType.LIKERT_SCALE | QuestionType.NPS_SCALE | QuestionType.YES_NO;
  scaleMin: number;
  scaleMax: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
};

// Choice-based questions (Multiple Choice, Multiple Select)
type ChoiceQuestion = BaseQuestion & {
  questionType: QuestionType.MULTIPLE_CHOICE | QuestionType.MULTIPLE_SELECT;
};

// Text-based questions (Short Text, Long Text)
type TextQuestion = BaseQuestion & {
  questionType: QuestionType.SHORT_TEXT | QuestionType.LONG_TEXT;
};

// Discriminated union of all question types
export type CustomSurveyQuestion = ScaleQuestion | ChoiceQuestion | TextQuestion;

export type CustomSurveyResponse = {
  id: number;
  uuid: string;
  title: string;
  description: string | null;
  templateId?: number;
  teamId?: number;
  organizationId?: number;
  allowAnonymous: boolean;
  isActive: boolean;
  expectedParticipantsCount: number;
  deadlineInDays: number;
  minimumResponseRate: number;
  analyzeStatus: AnalyzeStatus;
  createdAt: string;
  updatedAt: string;
  activatedAt: string | null;
  completedAt: string | null;
  responseCount?: number;
  responseRate?: number;
  questions: CustomSurveyQuestion[];
};

export type ScheduledCustomSurvey = {
  id: number;
  title: string;
  description: string | null;
  templateId: number;
  createdById: number;
  teamId: number;
  tenantId: number;
  deadlineInDays: number;
  minimumResponseRate: number;
  allowAnonymous: boolean;
  scheduledTime: string;
  frequency?: string;
  isActive: boolean;
  processingStartedAt: string | null;
  createdAt: string;
};

/**
 * Create a custom survey from a template
 */
export const createCustomSurveyFromTemplate = async (
  data: CreateCustomSurveyFromTemplateRequest,
): Promise<CustomSurveyResponse> => {
  return post<CustomSurveyResponse>('/api/custom-surveys/from-template', data);
};

/**
 * Get all custom surveys for a team
 */
export const getCustomSurveysByTeamId = async (
  teamId: number,
): Promise<CustomSurveyResponse[]> => {
  return get<CustomSurveyResponse[]>(
    `/api/custom-surveys?teamId=${teamId}`,
  );
};

/**
 * Get scheduled custom surveys for a team
 */
export const getScheduledCustomSurveys = async (
  teamId: number,
): Promise<ScheduledCustomSurvey[]> => {
  return get<ScheduledCustomSurvey[]>(
    `/api/schedule-custom-surveys/${teamId}`,
  );
};

/**
 * Delete a scheduled custom survey
 */
export const deleteScheduledCustomSurvey = async (
  teamId: number,
  surveyId: number,
): Promise<void> => {
  return del(`/api/schedule-custom-surveys/${teamId}/${surveyId}`);
};

// Public endpoints (no authentication required)

/**
 * Get custom survey by UUID for public access (anonymous)
 */
export const getCustomSurveyByUUID = async (
  uuid: string,
): Promise<CustomSurveyResponse> => {
  return get<CustomSurveyResponse>(`/api/custom-surveys/public/${uuid}`);
};

export type SubmitCustomSurveyAnswer = {
  questionId: number;
  numericValue?: number;
  textValue?: string;
  selectedOptions?: number[];
};

export type SubmitCustomSurveyRequest = {
  answers: SubmitCustomSurveyAnswer[];
};

export type SubmitCustomSurveyResponse = {
  id: number;
  customSurveyId: number;
  isAnonymous: boolean;
  submittedAt: string;
  message: string;
};

/**
 * Submit response to custom survey (public, no authentication required)
 */
export const submitCustomSurveyResponse = async (
  uuid: string,
  data: SubmitCustomSurveyRequest,
): Promise<SubmitCustomSurveyResponse> => {
  return post<SubmitCustomSurveyResponse>(
    `/api/custom-surveys/public/${uuid}/respond`,
    data,
  );
};

// Results and analytics

export type NpsDistribution = {
  detractors: number;
  passives: number;
  promoters: number;
  npsScore: number;
  detractorPercentage: number;
  passivePercentage: number;
  promoterPercentage: number;
};

export type QuestionStatistics = {
  questionId: number;
  questionText: string;
  questionType: QuestionType;
  category: string | null;
  responseCount: number;
  averageScore?: number;
  medianScore?: number;
  standardDeviation?: number;
  // Note: Backend sends Record<number, number>, but JSON serialization converts keys to strings
  optionDistribution?: Record<string, number>;
  npsDistribution?: NpsDistribution;
  options?: Array<{ id: number; optionText: string; orderIndex: number }>;
  textResponseCount?: number;
  chartType?: ChartType;
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
};

export type CustomSurveyResults = {
  id: number;
  uuid: string;
  title: string;
  description: string | null;
  templateId: number | null;
  teamId: number | null;
  isActive: boolean;
  isCompleted: boolean;
  expectedParticipantsCount: number;
  actualResponseCount: number;
  responseRate: number;
  minimumResponseRate: number;
  activatedAt?: string;
  completedAt?: string;
  /** Dager fra utsendelse til fristen */
  deadlineInDays?: number;
  viewerIsHrAdmin: boolean;
  questionStatistics: QuestionStatistics[];
};

/**
 * Get custom survey results with aggregated statistics (requires HR_ADMIN)
 */
export const getCustomSurveyResults = async (
  surveyId: number,
): Promise<CustomSurveyResults> => {
  return get<CustomSurveyResults>(`/api/custom-surveys/${surveyId}/results`);
};

/**
 * List custom surveys with optional filters
 */
export const listCustomSurveys = async (filters?: {
  teamId?: number;
  organizationId?: number;
  isActive?: boolean;
  templateId?: number;
}): Promise<CustomSurveyResponse[]> => {
  const params = new URLSearchParams();
  if (filters?.teamId !== undefined) params.append('teamId', String(filters.teamId));
  if (filters?.organizationId !== undefined) params.append('organizationId', String(filters.organizationId));
  if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
  if (filters?.templateId !== undefined) params.append('templateId', String(filters.templateId));

  const query = params.toString();
  return get<CustomSurveyResponse[]>(`/api/custom-surveys${query ? `?${query}` : ''}`);
};

export type QuestionTextResponses = {
  questionId: number;
  questionText: string;
  questionType: string;
  responseCount: number;
  textResponses: Array<{ id: number; textValue: string; submittedAt: string; isAnonymous: boolean }>;
};

/**
 * Get the text answers to one question in a survey
 */
export const getQuestionTextResponses = async (
  surveyId: number,
  questionId: number,
): Promise<QuestionTextResponses> => {
  return get<QuestionTextResponses>(`/api/custom-surveys/${surveyId}/questions/${questionId}/text-responses`);
};

// Template trends

export type TemplateQuestionTrendDataPoint = {
  surveyId: number;
  surveyTitle: string;
  surveyActivatedAt: string;
  averageScore?: number;
  medianScore?: number;
  standardDeviation?: number;
  // Note: Backend sends Record<number, number>, but JSON serialization converts keys to strings
  optionDistribution?: Record<string, number>;
  /** Alternativene i denne undersøkelsen, for å vise nøklene i optionDistribution som tekst */
  options?: Array<{ id: number; optionText: string; orderIndex: number }>;
  npsDistribution?: NpsDistribution;
  textResponses?: string[];
  responseCount: number;
};

export type TemplateQuestionTrends = {
  templateQuestionId: number;
  questionText: string;
  questionType: QuestionType;
  category: string | null;
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  chartType?: ChartType;
  trendData: TemplateQuestionTrendDataPoint[];
};

export type PendingSurveyInfo = {
  surveyId: number;
  surveyTitle: string;
  surveyActivatedAt: string;
  currentResponseCount: number;
  requiredResponseCount: number;
  teamMemberCount: number;
};

export type CustomSurveyTemplateTrends = {
  templateId: number;
  templateTitle: string;
  templateDescription: string | null;
  category: string;
  totalSurveysCreated: number;
  questionTrends: TemplateQuestionTrends[];
  pendingSurvey?: PendingSurveyInfo;
};

/**
 * Get template trends - aggregated data across all surveys from a template for a specific team
 */
export const getCustomSurveyTemplateTrends = async (
  templateId: number,
  teamId: number,
): Promise<CustomSurveyTemplateTrends> => {
  return get<CustomSurveyTemplateTrends>(
    `/api/custom-survey-template-trends/${templateId}?teamId=${teamId}`,
  );
};

// Category statistics

export type CategoryStatistics = {
  category: string;
  averageScore: number;
  standardDeviation?: number;
  responseCount: number;
  description: string;
};

export type CustomSurveyCategoryStats = {
  categoryStats: CategoryStatistics[];
  uniqueRespondents: number;
  totalActiveSurveys: number;
};

/**
 * Get custom survey category statistics
 * @param templateId - Optional template ID to filter stats by specific template
 * @param teamId - Optional team ID to filter stats by specific team
 * @param organizationId - Optional organization ID to filter stats by organization
 */
export const getCustomSurveyCategoryStats = async (
  templateId?: number,
  teamId?: number,
  organizationId?: number,
): Promise<CustomSurveyCategoryStats> => {
  const params = new URLSearchParams();
  if (templateId) params.append('templateId', templateId.toString());
  if (teamId) params.append('teamId', teamId.toString());
  if (organizationId) params.append('organizationId', organizationId.toString());

  const url = params.toString()
    ? `/api/custom-surveys/stats/categories?${params.toString()}`
    : '/api/custom-surveys/stats/categories';

  return get<CustomSurveyCategoryStats>(url);
};

// Individual responses

export type IndividualAnswer = {
  questionId: number;
  questionText: string;
  questionType: QuestionType;
  questionOrder: number;
  numericValue?: number | null;
  textValue?: string | null;
  selectedOptions?: string[] | null;
  category?: string | null;
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
};

export type IndividualResponse = {
  id: number;
  customSurveyId: number;
  respondentId: number | null;
  respondentEmail: string | null;
  isAnonymous: boolean;
  submittedAt: string;
  answers: IndividualAnswer[];
};

export type IndividualResponsesList = {
  responses: IndividualResponse[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type IndividualResponseDetail = {
  id: number;
  customSurveyId: number;
  surveyTitle: string;
  respondentId: number | null;
  respondentEmail: string | null;
  isAnonymous: boolean;
  submittedAt: string;
  answers: IndividualAnswer[];
  totalQuestions: number;
  answeredQuestions: number;
};

/**
 * Get paginated individual responses for a survey (requires HR_ADMIN)
 */
export const getIndividualResponses = async (
  surveyId: number,
  limit: number = 20,
  offset: number = 0,
): Promise<IndividualResponsesList> => {
  return get<IndividualResponsesList>(
    `/api/custom-surveys/${surveyId}/responses?limit=${limit}&offset=${offset}`,
  );
};

/**
 * Get detailed view of a single response (requires HR_ADMIN)
 */
export const getIndividualResponseDetail = async (
  surveyId: number,
  responseId: number,
): Promise<IndividualResponseDetail> => {
  return get<IndividualResponseDetail>(
    `/api/custom-surveys/${surveyId}/responses/${responseId}`,
  );
};
